import React, { useState, useMemo } from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { COLOR_SYSTEM } from "../../../components/design/design-system";
import DrugImage from "../../../components/molecules/DrugImage";
import { extractItemCode } from "../../../utils/drugImageUtils";
import { Drug, DrugSearchResult } from "../../../common/models/types";
import { patients, Reviewer, ReviewerComment, staticDrugData, discussionTopics, overviewData } from "./data";

// ── 자문위원 색상 ──
const REVIEWER_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  A: { bg: "#FFF3E0", border: "#FF9800", text: "#E65100" },
  B: { bg: "#E8F5E9", border: "#4CAF50", text: "#1B5E20" },
  C: { bg: "#E3F2FD", border: "#2196F3", text: "#0D47A1" },
  D: { bg: "#F3E5F5", border: "#9C27B0", text: "#4A148C" },
  E: { bg: "#FBE9E7", border: "#FF5722", text: "#BF360C" },
  F: { bg: "#E0F7FA", border: "#00ACC1", text: "#006064" },
};

// ── 타입 ──
interface CommentWithReviewer extends ReviewerComment {
  reviewerLabel: string;
  reviewerName: string;
}

/** 콘텐츠 파서가 만드는 개별 섹션 */
interface ContentSection {
  content: string;
  type: "intro" | "heading" | "separator" | "closing";
  pageBreak?: boolean;
}

/** page break 기준으로 묶인 "페이지" */
interface PageBlock {
  label: string; // "복약지도문", "공내과의원 자문" 등
  sections: ContentSection[];
  comments: CommentWithReviewer[]; // 이 페이지에 속하는 코멘트
}

// ── 콘텐츠를 섹션으로 분리 ──
function parseContentSections(content: string): ContentSection[] {
  const lines = content.split("\n");
  const sections: ContentSection[] = [];
  let buf: string[] = [];
  let bufType: ContentSection["type"] = "intro";

  const flush = () => {
    if (buf.length === 0) return;
    const text = buf.join("\n").trim();
    if (!text) { buf = []; return; }
    const isClosing =
      text.includes("다제약물관리 약사 드림") ||
      (text.includes("감사합니다") && !text.startsWith("#") && !text.startsWith("<badge>"));
    const hasPageBreak = text.includes("선생님께") && text.startsWith("<badge>");
    sections.push({
      content: text,
      type: isClosing && bufType !== "heading" ? "closing" : bufType,
      pageBreak: hasPageBreak,
    });
    buf = [];
  };

  for (const line of lines) {
    if (line.startsWith("# ")) {
      flush();
      buf = [line];
      bufType = "heading";
    } else if (line === "---") {
      flush();
      // separator는 page break 페이지 구분에서 무시
      sections.push({ content: "---", type: "separator" });
      bufType = "intro";
    } else if (line.startsWith("<badge>")) {
      flush();
      buf = [line];
      bufType = "intro";
    } else {
      buf.push(line);
    }
  }
  flush();
  return sections;
}

// ── 섹션들을 page break 기준으로 PageBlock으로 그룹핑 ──
function groupIntoPages(sections: ContentSection[]): PageBlock[] {
  const pages: PageBlock[] = [];
  let currentSections: ContentSection[] = [];
  let currentLabel = "복약지도문";

  const flush = () => {
    if (currentSections.length === 0) return;
    pages.push({ label: currentLabel, sections: currentSections, comments: [] });
    currentSections = [];
  };

  for (const section of sections) {
    if (section.type === "separator") continue; // ---는 건너뜀

    if (section.pageBreak) {
      flush();
      // badge에서 병원명 추출
      const match = section.content.match(/<badge>[^<]*?(\S+(?:병원|의원|센터))\s/);
      const hospitalMatch = section.content.match(/<badge>[^<]*?(\S+)\s+선생님께/);
      currentLabel = hospitalMatch?.[1] || match?.[1] || "자문 서신";
      currentLabel += " 자문";
    }

    currentSections.push(section);
  }
  flush();
  return pages;
}

// ── 코멘트를 섹션에 매칭 ──
function matchCommentsToSections(
  sections: ContentSection[],
  reviewers: Reviewer[],
  activeLabels: Set<string>
): Map<number, CommentWithReviewer[]> {
  const result = new Map<number, CommentWithReviewer[]>();

  for (const reviewer of reviewers) {
    if (!activeLabels.has(reviewer.label) || reviewer.comments.length === 0) continue;

    for (const comment of reviewer.comments) {
      let matchedIdx = -1;

      // 1) text 필드로 매칭 — 원본 text 그대로 먼저, extractSearchText fallback
      if (comment.text) {
        // 원본 text 그대로 매칭 (가장 정확)
        const rawText = comment.text.trim();
        if (rawText.length >= 4) {
          for (let i = 0; i < sections.length; i++) {
            if (sections[i].type === "separator") continue;
            if (sections[i].content.includes(rawText)) { matchedIdx = i; break; }
          }
        }
        // 실패 시 extractSearchText로 재시도
        if (matchedIdx === -1) {
          const exactSearch = extractSearchText(comment.text);
          if (exactSearch && exactSearch !== rawText) {
            for (let i = 0; i < sections.length; i++) {
              if (sections[i].type === "separator") continue;
              if (sections[i].content.includes(exactSearch)) { matchedIdx = i; break; }
            }
          }
        }
        // 정확 매칭 실패 시 단어 매칭 (3글자 이상 단어만, 2/3 이상 히트)
        if (matchedIdx === -1) {
          const search = comment.text.replace(/[~…"]/g, "").replace(/\(.*?\)/g, "").trim();
          const words = search.split(/[\s·,]+/).filter((w) => w.length >= 3);
          if (words.length >= 2) {
            for (let i = 0; i < sections.length; i++) {
              if (sections[i].type === "separator") continue;
              const sc = sections[i].content;
              const hits = words.filter((w) => sc.includes(w)).length;
              if (hits >= Math.ceil(words.length * 0.67)) { matchedIdx = i; break; }
            }
          }
        }
      }

      // 2) location 키워드 매칭
      if (matchedIdx === -1) {
        const loc = comment.location;
        const keywords = ["공내과", "참사랑", "정이비인후과", "튼튼정형외과", "해맑은내과",
          "모두병원", "더블루비뇨기과", "맑은이비인후과", "아름다운피부과", "미래종합병원",
          "A병원", "B병원", "C병원"];
        for (let i = 0; i < sections.length; i++) {
          const sc = sections[i].content;
          for (const kw of keywords) {
            if (loc.includes(kw) && sc.includes(kw)) { matchedIdx = i; break; }
          }
          if (matchedIdx !== -1) break;
        }
      }

      const list = result.get(matchedIdx) || [];
      list.push({ ...comment, reviewerLabel: reviewer.label, reviewerName: reviewer.name });
      result.set(matchedIdx, list);
    }
  }
  return result;
}

// ── 교수님 하이라이트: text에서 검색할 문자열 추출 ──
function extractSearchText(text: string): string | null {
  let s = text.replace(/^["']|["']$/g, "").trim();
  // ~ 앞부분만 사용 ("현재 하루 2번으로 처방되어 있으나~" → "현재 하루 2번으로 처방되어 있으나")
  if (s.includes("~")) s = s.split("~")[0].trim();
  s = s.replace(/…+$/, "").trim();
  if (s.length < 4) return null;
  return s;
}

// ── 콘텐츠에 <hl> 태그 주입 (교수님 하이라이트) ──
interface HighlightEntry {
  hlId: string;
  comment: string;
  reviewerLabel: string;
  reviewerName: string;
  location: string;
}

function injectHighlights(
  content: string,
  entries: HighlightEntry[],
  searchTexts: Map<string, string> // hlId → searchText
): string {
  let result = content;
  for (const entry of entries) {
    const searchText = searchTexts.get(entry.hlId);
    if (!searchText) continue;
    const idx = result.indexOf(searchText);
    if (idx !== -1) {
      result =
        result.substring(0, idx) +
        `<hl data-id="${entry.hlId}" data-label="${entry.reviewerLabel}">${searchText}</hl>` +
        result.substring(idx + searchText.length);
    }
  }
  return result;
}

// ══════════════════════════════════════════
//  스타일
// ══════════════════════════════════════════

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  background: ${COLOR_SYSTEM.get("Gray")[100]};
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 20px;
  color: ${COLOR_SYSTEM.get("Gray")[900]};
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`;

const PatientTabs = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const PatientTab = styled.button<{ $active: boolean }>`
  padding: 8px 20px;
  border-radius: 20px;
  border: 1.5px solid ${(p) => (p.$active ? COLOR_SYSTEM.get("Gray")[900] : COLOR_SYSTEM.get("Gray")[300])};
  background: ${(p) => (p.$active ? COLOR_SYSTEM.get("Gray")[900] : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : COLOR_SYSTEM.get("Gray")[700])};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: ${COLOR_SYSTEM.get("Gray")[900]}; }
`;

const PatientMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")[600]};
  span {
    background: ${COLOR_SYSTEM.get("Gray")[50]};
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid ${COLOR_SYSTEM.get("Gray")[200]};
  }
`;

const ReviewerToggleRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const ReviewerToggle = styled.button<{ $active: boolean; $color: string }>`
  padding: 6px 14px;
  border-radius: 16px;
  border: 1.5px solid ${(p) => (p.$active ? p.$color : COLOR_SYSTEM.get("Gray")[300])};
  background: ${(p) => (p.$active ? p.$color + "18" : "#fff")};
  color: ${(p) => (p.$active ? p.$color : COLOR_SYSTEM.get("Gray")[500])};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: ${(p) => p.$color}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// ── 페이지 블록 (page break 단위) ──
const PageCard = styled.div`
  background: #fff;
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const PageHeader = styled.div<{ $isFirst?: boolean }>`
  padding: 12px 24px;
  background: ${(p) => p.$isFirst ? COLOR_SYSTEM.get("Gray")[900] : COLOR_SYSTEM.get("Blue")[50]};
  color: ${(p) => p.$isFirst ? "#fff" : COLOR_SYSTEM.get("Blue")[700]};
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.3px;
`;

const PageBody = styled.div`
  padding: 0;
`;

// ── 각 heading 섹션 = 좌(원본) + 우(코멘트) ──
const SectionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionContent = styled.div`
  padding: 10px 24px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 10px 16px;
  }
`;

const SectionComments = styled.div`
  padding: 16px 16px;
  background: ${COLOR_SYSTEM.get("Gray")[30]};
  border-left: 1px solid ${COLOR_SYSTEM.get("Gray")[150]};

  @media (max-width: 768px) {
    border-left: none;
    border-top: 1px solid ${COLOR_SYSTEM.get("Gray")[150]};
    padding: 12px 16px;
  }
`;

const MarkdownBody = styled.div`
  p {
    margin: 5px 0;
    line-height: 1.7;
    font-size: 15px;
    color: ${COLOR_SYSTEM.get("Gray")[800]};
  }
  li p { margin: 4px 0; }

  h1 {
    font-size: 18px;
    font-weight: 700;
    line-height: 1.4;
    margin: 8px 0 6px 0;
    word-break: break-word;
    color: ${COLOR_SYSTEM.get("Gray")[900]};
  }
  h2 {
    font-size: 16px;
    font-weight: 700;
    margin: 8px 0 4px 0;
    color: ${COLOR_SYSTEM.get("Gray")[900]};
  }
  h3 {
    font-size: 15px;
    font-weight: 600;
    margin: 6px 0 4px 0;
    color: ${COLOR_SYSTEM.get("Gray")[800]};
  }

  strong { font-weight: 700; }

  ul, ol {
    margin: 4px 0;
    padding-left: 20px;
  }
  li {
    margin: 3px 0;
    font-size: 15px;
    line-height: 1.7;
  }

  blockquote {
    background: ${COLOR_SYSTEM.get("Gray")[10]};
    border: 0.5px solid ${COLOR_SYSTEM.get("Gray")[200]};
    border-radius: 10px;
    padding: 2px 14px;
    margin: 4px 0;
    font-size: 14px;
    line-height: 1.6;
    color: ${COLOR_SYSTEM.get("Gray")[700]};
  }

  hr {
    border: none;
    border-top: 1px solid ${COLOR_SYSTEM.get("Gray")[200]};
    margin: 24px 0;
  }

  mark[data-color="#1a1a1a"] {
    background-color: #1a1a1a;
    color: #ffffff;
    padding: 5px 16px;
    border-radius: 16px;
    display: inline-block;
    margin: 4px 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
  }

  /* 약품 이미지 카드 스타일 (인쇄 뷰어와 동일) */
  & [data-drug-container="true"] {
    gap: 10px !important;
    padding: 10px !important;
    margin: 6px 0 !important;
    grid-template-columns: 100px 1fr !important;
    border: 1px solid #ddd !important;
    border-radius: 8px !important;
    background: #f8f9fa !important;
    overflow: visible !important;
  }

  & [data-drug-container="true"] img {
    width: 100px !important;
    height: auto !important;
    min-width: 100px !important;
    object-fit: contain !important;
  }

  & [data-drug-container="true"] [data-drug-placeholder="true"] {
    width: 100px !important;
    height: ${Math.round(100 * 426 / 780)}px !important;
    min-width: 100px !important;
  }

  & [data-drug-name="true"] {
    font-size: 13px !important;
    font-weight: 600 !important;
    display: block !important;
    -webkit-line-clamp: unset !important;
    overflow: visible !important;
  }

  & [data-drug-chart="true"] {
    font-size: 12px !important;
    color: #555 !important;
  }

  & [data-drug-print="true"] {
    font-size: 12px !important;
    color: #555 !important;
  }
`;

const RefTag = styled.span`
  display: inline-block;
  background: ${COLOR_SYSTEM.get("Gray")[100]};
  color: ${COLOR_SYSTEM.get("Gray")[500]};
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 10px;
  margin: 0 2px;
  font-family: monospace;
`;

// ── 원문 하이라이트 (자문위원 색상별) ──
const InlineHighlight = styled.span<{ $active: boolean; $color: string; $colorLight: string }>`
  background: ${(p) => p.$active ? p.$colorLight + "55" : p.$colorLight + "22"};
  border-bottom: 2px solid ${(p) => p.$active ? p.$color : p.$color + "88"};
  padding: 0 2px;
  border-radius: 2px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
`;

// ── 코멘트 카드 (사이드) ──
const SideCommentCard = styled.div<{ $borderColor: string; $bgColor: string; $active?: boolean }>`
  border-left: 3px solid ${(p) => p.$borderColor};
  background: ${(p) => p.$bgColor};
  padding: 8px 10px;
  margin-bottom: 6px;
  border-radius: 0 6px 6px 0;
  font-size: 13px;
  transition: box-shadow 0.15s, transform 0.15s;
  ${(p) => p.$active && `
    box-shadow: 0 0 0 2px ${p.$borderColor};
    transform: scale(1.02);
    z-index: 1;
    position: relative;
  `}
`;

const SideCommentHeader = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")[500]};
  margin-bottom: 2px;
`;

const ReviewerBadge = styled.span<{ $color: string }>`
  font-weight: 700;
  color: ${(p) => p.$color};
  margin-right: 4px;
`;

const SideCommentQuote = styled.div`
  font-size: 11px;
  color: ${COLOR_SYSTEM.get("Gray")[500]};
  font-style: italic;
  margin-bottom: 3px;
`;

const SideCommentBody = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: ${COLOR_SYSTEM.get("Gray")[800]};
`;

const UnmatchedBlock = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px 24px;
  margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
`;

const UnmatchedLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")[500]};
  margin-bottom: 8px;
`;

// ── 개요 페이지 ──
const OverviewContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 24px 36px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const OverviewSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: ${COLOR_SYSTEM.get("Gray")[800]};
  font-weight: 500;
  margin-bottom: 32px;
  padding: 16px 20px;
  background: ${COLOR_SYSTEM.get("Gray")[50]};
  border-radius: 10px;
`;

const OverviewSection = styled.div`
  margin-bottom: 36px;
`;

const OverviewHeading = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")[900]};
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid ${COLOR_SYSTEM.get("Gray")[200]};
`;

const OverviewText = styled.p`
  font-size: 15px;
  line-height: 1.7;
  color: ${COLOR_SYSTEM.get("Gray")[700]};
  margin-bottom: 8px;
`;

const OverviewList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
  li {
    font-size: 15px;
    line-height: 1.8;
    color: ${COLOR_SYSTEM.get("Gray")[700]};
    margin-bottom: 4px;
  }
`;

const StepFlow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding: 8px 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }
`;

const StepItem = styled.div`
  flex: 1;
  text-align: center;
  position: relative;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 12px;
    text-align: left;
    min-width: unset;
    padding: 6px 0;
  }
`;

const StepLabel = styled.div`
  display: inline-block;
  padding: 8px 16px;
  background: ${COLOR_SYSTEM.get("Gray")[900]};
  color: #fff;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    margin-bottom: 0;
    white-space: nowrap;
    flex-shrink: 0;
    min-width: 80px;
    text-align: center;
  }
`;

const StepDesc = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: ${COLOR_SYSTEM.get("Gray")[600]};
  padding: 0 8px;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const StepArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: ${COLOR_SYSTEM.get("Gray")[300]};
  padding-top: 8px;

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 2px 0 2px 38px;
    justify-content: flex-start;
  }
`;

const PerspectiveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PerspectiveCard = styled.div`
  border: 1.5px solid ${COLOR_SYSTEM.get("Gray")[200]};
  border-radius: 10px;
  padding: 14px 18px;
  background: ${COLOR_SYSTEM.get("Gray")[10]};
`;

const PerspectiveLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")[900]};
  background: ${COLOR_SYSTEM.get("Gray")[100]};
  padding: 2px 8px;
  border-radius: 4px;
  margin-right: 8px;
`;

const PerspectiveQuestion = styled.span`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")[600]};
  font-style: italic;
`;

const GuideTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GuideRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: ${COLOR_SYSTEM.get("Gray")[10]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
`;

const GuideElement = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")[900]};
  min-width: 120px;
  flex-shrink: 0;
`;

const GuideDesc = styled.div`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")[600]};
`;

// ── 토론 주제 ──
const DiscussionItemBlock = styled.div`
  padding: 24px 0;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
  &:last-child { border-bottom: none; }
`;

const QuestionCard = styled.div`
  border: 1.5px solid ${COLOR_SYSTEM.get("Gray")[200]};
  border-radius: 10px;
  padding: 20px 24px;
  background: ${COLOR_SYSTEM.get("Gray")[10]};
  margin-bottom: 16px;
`;

const DiscussionLabel = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")[900]};
  margin-bottom: 10px;
`;

const DiscussionQuote = styled.div`
  border-left: 3px solid ${COLOR_SYSTEM.get("Gray")[300]};
  padding: 8px 14px;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 1.6;
  color: ${COLOR_SYSTEM.get("Gray")[500]};
  font-style: italic;
`;

const DiscussionQuestion = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: ${COLOR_SYSTEM.get("Gray")[700]};
`;

const DiscussionResultWrap = styled.div`
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.8;
  color: ${COLOR_SYSTEM.get("Gray")[800]};
`;

const ADVISOR_COLORS: Record<string, string> = {
  "1": "#E65100", // 주황
  "2": "#1B5E20", // 초록
  "3": "#0D47A1", // 파랑
  "4": "#4A148C", // 보라
  "5": "#BF360C", // 빨강
};

const AdvisorLine = styled.span<{ $color: string }>`
  color: ${(p) => p.$color};
  font-weight: 600;
`;

const ResultParagraph = styled.p`
  margin: 4px 0;
`;

const ConclusionBlock = styled.div`
  margin-top: 10px;
  padding: 10px 14px;
  border-left: 3px solid ${COLOR_SYSTEM.get("Gray")[400]};
  background: ${COLOR_SYSTEM.get("Gray")[30]};
  border-radius: 0 6px 6px 0;
  font-weight: 500;
`;

/** result 문자열을 파싱하여 자문위원별 색상 + 결론 블록으로 렌더링 */
function renderDiscussionResult(result: string) {
  const lines = result.split("\n");
  return (
    <DiscussionResultWrap>
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <br key={i} />;

        // 【결론】 또는 【...원칙...】 블록
        if (trimmed.startsWith("【결론")) {
          return <ConclusionBlock key={i}>{trimmed}</ConclusionBlock>;
        }

        // 자문위원N: 으로 시작하는 줄
        const advisorMatch = trimmed.match(/^(자문위원(\d)):(.*)$/);
        if (advisorMatch) {
          const num = advisorMatch[2];
          const color = ADVISOR_COLORS[num] || COLOR_SYSTEM.get("Gray")[700];
          return (
            <ResultParagraph key={i}>
              <AdvisorLine $color={color}>{advisorMatch[1]}:</AdvisorLine>
              {advisorMatch[3]}
            </ResultParagraph>
          );
        }

        // 【...】 라벨 (합의, 의견 분포 등)
        if (trimmed.startsWith("【")) {
          return <ResultParagraph key={i} style={{ fontWeight: 600 }}>{trimmed}</ResultParagraph>;
        }

        // 일반 줄 (• 포인트 등)
        return <ResultParagraph key={i}>{trimmed}</ResultParagraph>;
      })}
    </DiscussionResultWrap>
  );
}

// ══════════════════════════════════════════
//  컴포넌트
// ══════════════════════════════════════════

const SideComments: React.FC<{
  comments: CommentWithReviewer[];
  hoveredHlId: string | null;
  onHoverHl: (id: string | null) => void;
  hlIdMap: Map<string, string>; // `${label}-${commentId}` → hlId
}> = ({ comments, hoveredHlId, onHoverHl, hlIdMap }) => (
  <>
    {comments.map((c, idx) => {
      const colors = REVIEWER_COLORS[c.reviewerLabel];
      const hlId = hlIdMap.get(`${c.reviewerLabel}-${c.id}`);
      const isActive = !!hlId && hoveredHlId === hlId;
      return (
        <SideCommentCard
          key={`${c.reviewerLabel}-${c.id}-${idx}`}
          $borderColor={colors.border}
          $bgColor={colors.bg}
          $active={isActive}
          data-hl-id={hlId}
          onMouseEnter={() => hlId && onHoverHl(hlId)}
          onMouseLeave={() => hlId && onHoverHl(null)}
        >
          <SideCommentHeader>
            <ReviewerBadge $color={colors.text}>{c.reviewerLabel}</ReviewerBadge>
            {c.reviewerName && <span>{c.reviewerName}</span>}
          </SideCommentHeader>
          {c.text && <SideCommentQuote>"{c.text}"</SideCommentQuote>}
          <SideCommentBody>{c.comment}</SideCommentBody>
        </SideCommentCard>
      );
    })}
  </>
);

// ── 메인 ──
const MedicationReviewPage: React.FC = () => {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [activeReviewers, setActiveReviewers] = useState<Set<string>>(new Set(["A", "B", "C", "D", "E", "F"]));
  const [hoveredHlId, setHoveredHlId] = useState<string | null>(null);

  const patient = selectedIdx >= 1 && selectedIdx <= 4 ? patients[selectedIdx - 1] : null;
  const totalComments = patient?.reviewers.reduce((s, r) => s + r.comments.length, 0) ?? 0;

  // 정적 약품 데이터 → Drug[] / DrugSearchResult[] 형태로 변환
  const drugsData = useMemo<Drug[]>(() =>
    Object.entries(staticDrugData).map(([itemCode, info]) => ({
      itemCode,
      productName: info.productName,
    } as Drug)),
  []);

  const pillsData = useMemo<DrugSearchResult[]>(() =>
    Object.entries(staticDrugData)
      .filter(([, info]) => info.chart || info.printFront || info.printBack)
      .map(([itemCode, info]) => ({
        itemSeq: itemCode,
        chart: info.chart,
        printFront: info.printFront,
        printBack: info.printBack,
      } as DrugSearchResult)),
  []);

  // ── 텍스트 노드 변환 (drugsData/pillsData 클로저) ──
  const processText = useMemo(() => {
    return (text: string): React.ReactNode[] => {
      const parts = text.split(/(⑆img_[A-Za-z0-9]+|\{[^}]*⑆[^}]*\})/g);
      return parts.map((part, i) => {
        if (part.startsWith("⑆img_")) {
          const itemCode = extractItemCode(part);
          return (
            <DrugImage
              key={i}
              itemCode={itemCode}
              drugsData={drugsData}
              pillsData={pillsData}
              width={100}
              enableHoverZoom={false}
            />
          );
        }
        if (part.startsWith("{") && part.includes("⑆")) {
          const refs = part.replace(/[{}]/g, "").trim().split(/\s+/).filter(Boolean);
          return (
            <span key={i} style={{ display: "inline" }}>
              {refs.map((ref, j) => <RefTag key={j}>{ref.replace("⑆", "")}</RefTag>)}
            </span>
          );
        }
        return part;
      });
    };
  }, [drugsData, pillsData]);

  // ── 전체 자문위원 하이라이트 엔트리 빌드 ──
  const { highlightEntries, hlSearchTexts, hlIdMap } = useMemo(() => {
    const entries: HighlightEntry[] = [];
    const searchTexts = new Map<string, string>();
    const idMap = new Map<string, string>(); // `${label}-${commentId}` → hlId

    for (const reviewer of (patient?.reviewers || [])) {
      if (!activeReviewers.has(reviewer.label)) continue;
      for (const c of reviewer.comments) {
        if (!c.text) continue;
        const search = extractSearchText(c.text);
        if (!search) continue;
        const hlId = `hl-${reviewer.label}-${c.id}`;
        entries.push({
          hlId,
          comment: c.comment,
          reviewerLabel: reviewer.label,
          reviewerName: reviewer.name,
          location: c.location,
        });
        searchTexts.set(hlId, search);
        idMap.set(`${reviewer.label}-${c.id}`, hlId);
      }
    }
    return { highlightEntries: entries, hlSearchTexts: searchTexts, hlIdMap: idMap };
  }, [patient?.reviewers, activeReviewers]);

  // ── ReactMarkdown 컴포넌트 ──
  const markdownComponents = useMemo(() => ({
    badge: ({ children }: any) => (
      <mark data-color="#1a1a1a" style={{
        backgroundColor: "#1a1a1a", padding: "4px 14px", borderRadius: "15px",
        display: "inline-block", margin: "12px 0 8px 0", textDecoration: "none",
      }}>
        <span style={{ color: "#ffffff", fontSize: "14px", fontWeight: "600" }}>{children}</span>
      </mark>
    ),
    // <hl data-id="..." data-label="..."> 처리 — 자문위원 하이라이트
    hl: ({ children, ...props }: any) => {
      const id = props["data-id"];
      const label = props["data-label"] || "A";
      const colors = REVIEWER_COLORS[label] || REVIEWER_COLORS.A;
      return (
        <InlineHighlight
          $active={hoveredHlId === id}
          $color={colors.border}
          $colorLight={colors.bg}
          onMouseEnter={() => setHoveredHlId(id)}
          onMouseLeave={() => setHoveredHlId(null)}
        >
          {children}
        </InlineHighlight>
      );
    },
    p: ({ children }: any) => {
      const childArr = Array.isArray(children) ? children : [children];
      const processed = childArr.flatMap((child: any, idx: number) => {
        if (typeof child === "string") {
          if (child.trim().match(/^(⑆img_[A-Za-z0-9]+\s*)+$/)) {
            return child.trim().split(/\s+/).filter((s: string) => s.startsWith("⑆img_"))
              .map((code: string, j: number) => (
                <DrugImage
                  key={`img-${idx}-${j}`}
                  itemCode={extractItemCode(code)}
                  drugsData={drugsData}
                  pillsData={pillsData}
                  width={100}
                  enableHoverZoom={false}
                />
              ));
          }
          return processText(child);
        }
        return child;
      });
      const allImages = processed.every(
        (c: any) => React.isValidElement(c) && (c as any).type === DrugImage
      );
      if (allImages) return <>{processed}</>;
      return <p>{processed}</p>;
    },
    li: ({ children }: any) => {
      const childArr = Array.isArray(children) ? children : [children];
      const processed = childArr.flatMap((child: any) =>
        typeof child === "string" ? processText(child) : child
      );
      return <li>{processed}</li>;
    },
  }), [drugsData, pillsData, processText, hoveredHlId]);

  // 1) 섹션 파싱 (원본 + 하이라이트 주입 버전)
  const rawSections = useMemo(() => parseContentSections(patient?.content || ""), [patient?.content]);

  const sections = useMemo(() => {
    if (highlightEntries.length === 0) return rawSections;
    return rawSections.map((s) => ({
      ...s,
      content: injectHighlights(s.content, highlightEntries, hlSearchTexts),
    }));
  }, [rawSections, highlightEntries, hlSearchTexts]);

  // 2) 코멘트 매칭 — 원본(하이라이트 주입 전) sections으로 매칭
  const commentMap = useMemo(
    () => matchCommentsToSections(rawSections, patient?.reviewers || [], activeReviewers),
    [rawSections, patient?.reviewers, activeReviewers]
  );

  // 3) 페이지 블록 그룹핑 + 코멘트 분배
  const pageBlocks = useMemo(() => {
    const pages = groupIntoPages(sections);

    // 각 페이지의 섹션들에 매칭된 코멘트를 모아줌
    for (const page of pages) {
      const pageComments: CommentWithReviewer[] = [];
      for (const section of page.sections) {
        const sectionIdx = sections.indexOf(section);
        const matched = commentMap.get(sectionIdx) || [];
        pageComments.push(...matched);
      }
      page.comments = pageComments;
    }

    return pages;
  }, [sections, commentMap]);

  const unmatchedComments = commentMap.get(-1) || [];

  const toggleReviewer = (label: string) => {
    setActiveReviewers((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  };

  const patientIdx = selectedIdx - 1;

  return (
    <PageContainer>
      <PageTitle>{overviewData.title}</PageTitle>

      <TopBar>
        <PatientTabs>
          <PatientTab $active={selectedIdx === 0} onClick={() => { setSelectedIdx(0); window.scrollTo(0, 0); }} style={{ borderColor: selectedIdx === 0 ? "#455A64" : undefined, background: selectedIdx === 0 ? "#455A64" : undefined, color: selectedIdx === 0 ? "#fff" : undefined }}>
            개요
          </PatientTab>
          {patients.map((p, idx) => (
            <PatientTab key={p.patientId} $active={idx + 1 === selectedIdx} onClick={() => setSelectedIdx(idx + 1)}>
              {p.label}
            </PatientTab>
          ))}
          <PatientTab $active={selectedIdx === 5} onClick={() => setSelectedIdx(5)} style={{ borderColor: selectedIdx === 5 ? "#1976D2" : undefined, background: selectedIdx === 5 ? "#1976D2" : undefined, color: selectedIdx === 5 ? "#fff" : undefined }}>
            토론
          </PatientTab>
        </PatientTabs>
        {selectedIdx >= 1 && selectedIdx <= 4 && (
          <PatientMeta>
            <span>환자ID: {patient?.patientId}</span>
            <span>보고서ID: {patient?.reportId}</span>
            <span>첨삭 {totalComments}건</span>
          </PatientMeta>
        )}
      </TopBar>

      {selectedIdx === 0 ? (
        <PageCard>
          <PageBody>
            <OverviewContainer>
              <OverviewSubtitle>{overviewData.subtitle}</OverviewSubtitle>
              {overviewData.sections.map((section, sIdx) => (
                <OverviewSection key={sIdx}>
                  <OverviewHeading>{section.heading}</OverviewHeading>
                  {section.content && <OverviewText>{section.content}</OverviewText>}
                  {section.items && (
                    <OverviewList>
                      {section.items.map((item, i) => <li key={i}>{item}</li>)}
                    </OverviewList>
                  )}
                  {section.steps && (
                    <StepFlow>
                      {section.steps.map((step, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <StepArrow>→</StepArrow>}
                          <StepItem>
                            <StepLabel>{step.label}</StepLabel>
                            <StepDesc>{step.description}</StepDesc>
                          </StepItem>
                        </React.Fragment>
                      ))}
                    </StepFlow>
                  )}
                  {section.perspectives && (
                    <PerspectiveGrid>
                      {section.perspectives.map((p, i) => (
                        <PerspectiveCard key={i}>
                          <PerspectiveLabel>{p.perspective}</PerspectiveLabel>
                          <PerspectiveQuestion>"{p.question}"</PerspectiveQuestion>
                        </PerspectiveCard>
                      ))}
                    </PerspectiveGrid>
                  )}
                  {section.guideItems && (
                    <GuideTable>
                      {section.guideItems.map((g, i) => (
                        <GuideRow key={i}>
                          <GuideElement>{g.element}</GuideElement>
                          <GuideDesc>{g.description}</GuideDesc>
                        </GuideRow>
                      ))}
                    </GuideTable>
                  )}
                </OverviewSection>
              ))}
              <div style={{ textAlign: "center", marginTop: 24, marginBottom: 8 }}>
                <PatientTab as="button" $active onClick={() => { setSelectedIdx(1); window.scrollTo(0, 0); }} style={{ padding: "12px 32px", fontSize: 16, cursor: "pointer" }}>
                  환자1 자문내용 보기 →
                </PatientTab>
              </div>
            </OverviewContainer>
          </PageBody>
        </PageCard>
      ) : selectedIdx === 5 ? (
        <>
          {discussionTopics.map((topic, topicIdx) => (
            <PageCard key={topicIdx}>
              <PageHeader $isFirst>{topic.title}</PageHeader>
              <PageBody>
                <SectionContent style={{ padding: "24px 36px" }}>
                  {topic.items.map((item, itemIdx) => (
                    <DiscussionItemBlock key={itemIdx}>
                      <QuestionCard>
                        <DiscussionLabel>{item.label}</DiscussionLabel>
                        {item.quote && <DiscussionQuote>"{item.quote}"</DiscussionQuote>}
                        {item.question && <DiscussionQuestion>{item.question}</DiscussionQuestion>}
                      </QuestionCard>
                      {item.result && renderDiscussionResult(item.result)}
                    </DiscussionItemBlock>
                  ))}
                </SectionContent>
              </PageBody>
            </PageCard>
          ))}
          <div style={{ textAlign: "center", marginTop: 24, marginBottom: 8 }}>
            <PatientTab as="a" $active href="https://phai.ymyd.co.kr" target="_blank" rel="noopener noreferrer" style={{ padding: "12px 32px", fontSize: 16, cursor: "pointer", textDecoration: "none", borderColor: "#1976D2", background: "#1976D2", color: "#fff" }}>
              PhAI 사용하러 가기 →
            </PatientTab>
          </div>
        </>
      ) : (
      <>
      <ReviewerToggleRow>
        {(patient?.reviewers || []).map((r) => {
          const colors = REVIEWER_COLORS[r.label];
          const name = r.name
            ? `${r.label}. ${r.name}${r.affiliation ? ` (${r.affiliation})` : ""}`
            : `자문위원 ${r.label}`;
          return (
            <ReviewerToggle
              key={r.label}
              $active={activeReviewers.has(r.label)}
              $color={colors.border}
              onClick={() => toggleReviewer(r.label)}
              disabled={r.comments.length === 0}
            >
              {name} ({r.comments.length})
            </ReviewerToggle>
          );
        })}
      </ReviewerToggleRow>

      {/* 페이지 블록별 렌더링 */}
      {pageBlocks.map((page, pageIdx) => {
        // 이 페이지의 섹션별 코멘트 매핑
        const sectionCommentPairs: Array<{ section: ContentSection; comments: CommentWithReviewer[] }> = [];
        for (const section of page.sections) {
          if (section.type === "separator") continue;
          const sectionIdx = sections.indexOf(section);
          const matched = commentMap.get(sectionIdx) || [];
          sectionCommentPairs.push({ section, comments: matched });
        }

        return (
          <PageCard key={pageIdx}>
            <PageHeader $isFirst={pageIdx === 0}>
              {pageIdx === 0 ? "환자용 복약지도문" : page.label}
            </PageHeader>
            <PageBody>
              {sectionCommentPairs.map(({ section, comments }, rowIdx) => (
                  <SectionRow key={rowIdx}>
                    <SectionContent>
                      <MarkdownBody>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={markdownComponents}
                        >
                          {section.content}
                        </ReactMarkdown>
                      </MarkdownBody>
                    </SectionContent>
                    <SectionComments>
                      {comments.length > 0 && (
                        <SideComments
                          comments={comments}
                          hoveredHlId={hoveredHlId}
                          onHoverHl={setHoveredHlId}
                          hlIdMap={hlIdMap}
                        />
                      )}
                    </SectionComments>
                  </SectionRow>
              ))}
            </PageBody>
          </PageCard>
        );
      })}

      {/* 매칭 안 된 코멘트 */}
      {unmatchedComments.length > 0 && (
        <UnmatchedBlock>
          <UnmatchedLabel>기타 첨삭 (위치 미매칭)</UnmatchedLabel>
          {unmatchedComments.map((c, idx) => {
            const colors = REVIEWER_COLORS[c.reviewerLabel];
            return (
              <SideCommentCard key={idx} $borderColor={colors.border} $bgColor={colors.bg}>
                <SideCommentHeader>
                  <ReviewerBadge $color={colors.text}>{c.reviewerLabel}</ReviewerBadge>
                  {c.reviewerName} <span style={{ color: COLOR_SYSTEM.get("Gray")[400], marginLeft: 6 }}>{c.location}</span>
                </SideCommentHeader>
                {c.text && <SideCommentQuote>"{c.text}"</SideCommentQuote>}
                <SideCommentBody>{c.comment}</SideCommentBody>
              </SideCommentCard>
            );
          })}
        </UnmatchedBlock>
      )}
      <div style={{ textAlign: "center", marginTop: 24, marginBottom: 8 }}>
        <PatientTab as="button" $active onClick={() => { setSelectedIdx(selectedIdx + 1); window.scrollTo(0, 0); }} style={{ padding: "12px 32px", fontSize: 16, cursor: "pointer" }}>
          {selectedIdx <= 3 ? `환자${selectedIdx + 1} 자문내용 보기 →` : "토론 보기 →"}
        </PatientTab>
      </div>
      </>
      )}
    </PageContainer>
  );
};

export default MedicationReviewPage;
